import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { InfoOutlined, WarningAmberRounded } from '@mui/icons-material';
import { useGlobalization } from '@Translations/useGlobalization';
import {
	ARTIFAKT_FONT,
	RISK_ONLY_GUIDANCE,
	scoreGuidance,
} from './scoreGuidance';
import { ScoreLegend } from './ScoreLegend';

interface Props {
	componentLabel?: string;
	// Source field the industry reference is keyed on (not the typed name).
	fieldName?: string;
	coveragePct?: number;
	totalAssets?: number;
	coverageEligible?: boolean;
}

export const ScoreSetupHeader = ({
	componentLabel,
	fieldName,
	coveragePct,
	totalAssets,
	coverageEligible = true,
}: Props) => {
	const { t } = useGlobalization();
	const heading = componentLabel
		? t('Configure Scores for: {{name}}', { name: componentLabel })
		: t('Score Setup');
	const showCoverage =
		coverageEligible &&
		coveragePct !== undefined &&
		totalAssets !== undefined &&
		totalAssets > 0;
	const low = coveragePct !== undefined && coveragePct < 35;
	const miss =
		coveragePct === undefined
			? 0
			: Math.round((100 - coveragePct) * 10) / 10;

	return (
		<Box sx={{ fontFamily: ARTIFAKT_FONT }}>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 1.25,
					flexWrap: 'wrap',
				}}>
				<Typography
					sx={{ fontSize: 18, fontWeight: 700, color: '#0d1520' }}>
					{heading}
				</Typography>
				{showCoverage && (
					<Tooltip
						arrow
						title={
							low
								? t(
										'Only {{cov}}% of assets have data for this component. The remaining {{miss}}% will use the score you assign to "--".',
										{ cov: coveragePct, miss },
								  )
								: t(
										'{{cov}}% of assets have data for this component. The rest use the score you assign to "--".',
										{ cov: coveragePct },
								  )
						}>
						<Box
							component="span"
							sx={{
								display: 'inline-flex',
								alignItems: 'center',
								gap: 0.5,
								px: 1,
								py: '3px',
								borderRadius: '9999px',
								fontSize: 12,
								fontWeight: 700,
								color: '#3a4451',
								background: '#eef1f4',
								whiteSpace: 'nowrap',
								cursor: 'default',
							}}>
							{low ? (
								<WarningAmberRounded
									sx={{ fontSize: 14, color: '#e0872f' }}
								/>
							) : (
								<InfoOutlined
									sx={{ fontSize: 14, color: '#97a0ac' }}
								/>
							)}
							{coveragePct}% {t('coverage')}
							{totalAssets === undefined
								? ''
								: ` · ${totalAssets.toLocaleString()} ${t(
										'assets',
								  )}`}
						</Box>
					</Tooltip>
				)}
			</Box>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'flex-start',
					gap: 0.75,
					mt: 1,
				}}>
				<InfoOutlined
					sx={{ fontSize: 15, color: '#97a0ac', mt: '1px' }}
				/>
				<Typography sx={{ fontSize: 12.5, color: '#5b6472' }}>
					{t(
						coverageEligible
							? scoreGuidance(fieldName, showCoverage)
							: RISK_ONLY_GUIDANCE,
					)}
				</Typography>
			</Box>
			<Box sx={{ mt: 1.5 }}>
				<ScoreLegend />
			</Box>
		</Box>
	);
};

export default ScoreSetupHeader;
